import { GL } from '@luma.gl/constants';

import type {
  ColorRGB,
  ConfigKey,
  MapConfigState,
  MapConfigTrainCategory,
  ToggleOption,
} from '@/types';

import { createCircleMapping } from '@/utils/helpers/train-simulation/create-circle-mapping';
import {
  FLAG_CANVAS_HEIGHT,
  FLAG_CANVAS_WIDTH,
} from '@/utils/helpers/train-simulation/create-flag-atlas';
import {
  createAnchoredMapping,
  createMapping,
} from '@/utils/helpers/train-simulation/create-mapping';

export const MAP_BOUNDARY_DEFAULT: [[number, number], [number, number]] = [
  [9.0, 46.2],
  [10.15, 47.0],
];

export const MAP_BOUNDARY_MOBILE: [[number, number], [number, number]] = [
  [9.15, 46.2],
  [9.99, 47.0],
];

export const MOBILE_INITIAL_ZOOM_OFFSET = 0.8;
export const MAX_ZOOM_IN = 14.99;
export const MAX_ZOOM_OUT = 7;
export const ZOOM_8 = 8;
export const ZOOM_9 = 9;
export const ZOOM_10 = 10;
export const ZOOM_11 = 11;
export const ZOOM_13 = 13;

export const MAPBOX_INITIAL_VIEW_STATE = {
  // Chur’s Altstadt, Switzerland
  latitude: 46.6031,
  longitude: 9.6043,
  zoom: MAX_ZOOM_OUT,
};

export const STATION_MARKER = 'station-marker';

export const STATION_SOURCE = 'station';

export const TRAIN_CIRCLE_TEXT_LAYER = 'train-circle-text-layer';
export const TRAIN_CIRCLE_LAYER = 'train-circle-layer';
export const TRAIN_CIRCLE_BORDER_LAYER = 'train-circle-border-layer';

export const STATION_LAYER = 'station-layer';
export const SLOW_SECTION_LAYER = 'slow-section-layer';

export const LOCKED_PIN_ICON_LAYER = 'locked-pin-icon-layer';
export const LOCKED_PIN_BORDER_LAYER = 'locked-pin-border-layer';
export const LOCKED_PIN_BG_LAYER = 'locked-pin-bg-layer';

export const LOCKED_CIRCLE_LAYER = 'locked-circle-layer';
export const LOCKED_JOURNEY_NUMBER_LAYER = 'locked-journey-number-layer';

export const DEVIATION_BADGE_LAYER = 'deviation-badge-layer';

export const JOURNEY_NUMBER_BADGE_LAYER = 'journey-number-badge-layer';

export const DIRECTION_TORCH_LAYER = 'direction-torch-layer';
export const FLAG_BOTTOM_LAYER = 'flag-bottom-layer';
export const FLAG_TOP_LAYER = 'flag-top-layer';

export const VEREINA_BADGE_BORDER_LAYER = 'vereina-badge-border-layer';
export const VEREINA_BADGE_BG_LAYER = 'vereina-badge-bg-layer';
export const VEREINA_BADGE_TEXT_LAYER = 'vereina-badge-text-layer';
export const VEREINA_BADGE_OFFSET: [number, number] = [24, -20];

export const VEREINA_COLOR_MAP: Record<string, string> = {
  '186,232,184': 'bg-deviation-ontime-subtle text-deviation-ontime-subtle-foreground',
  '245,207,163': 'bg-deviation-delay-subtle text-deviation-delay-subtle-foreground',
  '243,205,209': 'bg-deviation-too-late-subtle text-deviation-too-late',
  '231,231,231': 'bg-secondary text-foreground',
};

export const INTERACTIVE_LAYERS = [TRAIN_CIRCLE_LAYER, STATION_LAYER];

// Train simulation
export const ARRIVAL_THRESHOLD = 0.995;
export const DEPARTURE_THRESHOLD = 0.005;

export const TRAIN_DIAMETER_SMALL = 12;
export const TRAIN_DIAMETER_LARGE = 42;

export const MAX_POOL_SIZE = 4000;

export const POOL_CLEANUP_INTERVAL_FRAMES = 600; // ~10 seconds at 60fps
export const MIN_POOL_CAPACITY = 100;
export const POOL_BUFFER_SIZE = 50;

export const PIN_W = 32;
export const PIN_H = 26;
export const ICON_SIZE = 16;
export const OFFSET_PIN_X = 26;
export const OFFSET_PIN_Y = -26;

export const SCATTER_RADIUS = 0.0008; // ~90m radius
export const SCATTER_START_ANGLE = 90;

export const Z_STACK_STEP = 5;

export const HUD_PARAMETERS = {
  [GL.DEPTH_TEST]: true,
  [GL.DEPTH_FUNC]: GL.LEQUAL,
} as Record<number, boolean | number>;

export const TEXT_STYLE = {
  fontFamily: '"Inter", system-ui, sans-serif',
  fontWeight: 600,
  size: 12,
};

export const CIRCLE_BG_COLORS: Record<string, ColorRGB> = {
  early: [54, 119, 176],
  onTime: [45, 162, 84],
  delayed: [229, 134, 25],
  tooLate: [188, 8, 28],
  stopped: [136, 136, 136],
  default: [54, 119, 176],
};

export const DEVIATION_BADGE_BG_COLORS: Record<string, ColorRGB> = {
  early: [190, 218, 242],
  onTime: [186, 232, 184],
  delayed: [245, 207, 163],
  tooLate: [243, 205, 209],
  default: [190, 218, 242],
};

export const DEVIATION_BADGE_TEXT_COLORS: Record<string, ColorRGB> = {
  early: [0, 61, 113],
  onTime: [6, 96, 0],
  delayed: [85, 29, 0],
  tooLate: [105, 0, 12],
  default: [44, 62, 80],
};

export const TORCH_SIZE = 120;

export const DEPTH = { TEXT: -3, BADGE: -2, CIRCLE: -1, TORCH: 1 };

export const OFFSETS = {
  JOURNEY: 30,
  DEVIATION: -22,
  CIRCLE: 0,
  TORCH: 0,
};

export const MAPPINGS = {
  JOURNEY: createMapping(42, 20),
  JOURNEY_WITH_DANGEROUS_WAGON: createMapping(44, 16),
  DEVIATION: createMapping(30, 20),
  CIRCLE: createCircleMapping(32),
  RING: createCircleMapping(64),
  PIN_SHAPE: createMapping(PIN_W, PIN_H),
  PIN_ICON: createMapping(ICON_SIZE, ICON_SIZE),
  TORCH: createMapping(TORCH_SIZE, TORCH_SIZE),
  FLAG_BOTTOM: createMapping(FLAG_CANVAS_WIDTH / 2, FLAG_CANVAS_HEIGHT / 2),
  FLAG_TOP: createMapping(FLAG_CANVAS_WIDTH / 2, FLAG_CANVAS_HEIGHT / 2),
  VEREINA_BADGE: createMapping(40, 24),
  SLOW_SECTION: createAnchoredMapping(103, 178, 60, 200),
  STATION_MARKER: createAnchoredMapping(80, 80, 40, 40),
  DANGEROUS_MARKER: createAnchoredMapping(32, 32, 0, 0),
};

export const DEFAULT_LOCATION_LABEL = 'common.current_location';

export const GEO_ERRORS = {
  OUTSIDE_NETWORK: 'errors.map.outside_network',
  PERMISSION_DENIED: 'errors.map.permission_denied',
  POSITION_UNAVAILABLE: 'errors.map.position_unavailable',
  NOT_SUPPORTED: 'errors.map.not_supported',
  UNKNOWN: 'errors.map.unknown',
};

const RHB_NETWORK_BOUNDS_TOLERANCE = 0.01; // to cover flying to the border areas
export const RHB_NETWORK_DEFAULT_CENTER: [number, number] = [9.555776, 46.969188]; // Landquart

const RHB_NETWORK_BOUNDARY_DEFAULT: [[number, number], [number, number]] = [
  [8.85, 46.21],
  [10.3, 46.98],
];

export const RHB_NETWORK_SEARCH_BOUNDS = {
  minX: RHB_NETWORK_BOUNDARY_DEFAULT[0][0] - RHB_NETWORK_BOUNDS_TOLERANCE,
  minY: RHB_NETWORK_BOUNDARY_DEFAULT[0][1] - RHB_NETWORK_BOUNDS_TOLERANCE,
  maxX: RHB_NETWORK_BOUNDARY_DEFAULT[1][0] + RHB_NETWORK_BOUNDS_TOLERANCE,
  maxY: RHB_NETWORK_BOUNDARY_DEFAULT[1][1] + RHB_NETWORK_BOUNDS_TOLERANCE,
};

export const TRAIN_CATEGORY_OPTIONS: ToggleOption[] = [
  { key: 'passengerTrains', label: 'map.actions.configuration.train_category.passenger_trains' },
  { key: 'carTrains', label: 'map.actions.configuration.train_category.car_trains' },
  { key: 'goodsTrains', label: 'map.actions.configuration.train_category.goods_trains' },
  { key: 'serviceTrains', label: 'map.actions.configuration.train_category.service_trains' },
];

export const GENERAL_OPTIONS: ToggleOption[] = [
  { key: 'slowSections', label: 'map.actions.configuration.general.slow_sections' },
  {
    key: 'nonCommercialStations',
    label: 'map.actions.configuration.general.non_commercial_stations',
  },
];

export const INITIAL_MAP_CONFIG: MapConfigState = {
  passengerTrains: true,
  carTrains: true,
  goodsTrains: true,
  serviceTrains: true,
  slowSections: true,
  nonCommercialStations: true,
  hideDetails: false,
};

export const CATEGORY_TO_CONFIG_KEY: Record<MapConfigTrainCategory, ConfigKey> = {
  passenger_train: 'passengerTrains',
  car_train: 'carTrains',
  goods_train: 'goodsTrains',
  service_train: 'serviceTrains',
};

// Train pin animation
export const PIN_POSITION_THRESHOLD = 0.00001;
export const PIN_BEARING_THRESHOLD = 0.5;
export const PIN_FOLLOW_INTERVAL_MS = 3000;

// Locked train cleanup
export const CLEANUP_CHECK_INTERVAL_MS = 3000;

export const COLOR_GRAY: ColorRGB = [132, 132, 132];
export const COLOR_SIGNED_ON: ColorRGB = [229, 229, 229];
export const COLOR_WHITE: ColorRGB = [255, 255, 255];
export const COLOR_RED: ColorRGB = [235, 10, 35];
export const COLOR_FLAG_BLUE: ColorRGB = [54, 106, 180];

export const OFFSET_JOURNEY: [number, number] = [0, OFFSETS.JOURNEY];
export const OFFSET_DEVIATION: [number, number] = [16, OFFSETS.DEVIATION];
export const OFFSET_CIRCLE: [number, number] = [0, OFFSETS.CIRCLE];
export const OFFSET_TORCH: [number, number] = [0, OFFSETS.TORCH];
export const POLYGON_OFFSET_TEXT = (): [number, number] => [0, DEPTH.TEXT];

export const characterSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789:+- ';
